/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { Cancel, CheckCircle, Delete, Edit } from '@mui/icons-material';
import Add from '@mui/icons-material/Add';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import {
  HUMaterialTable,
  HUMaterialTableProps,
  iconCellStyles,
} from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../i18n';
import { SuperuserSupportPlanApiService } from '../../services/SuperuserSupportPlanApiService';
import { useMiscellaneousStyles } from '../../styles/miscellaneous';
import { ApiClientError } from '../../types/api/ApiClientError';
import { SupportPlanResponse } from '../../types/common/PlanSupport';
import { SystemConfiguration } from '../../types/superuser/SystemConfiguration';
import { fetchSupportPlans, fetchSystemConfiguration } from '../../utils/queryFns';
import { reactToAdminSupportPlanAction } from '../../utils/reactions';
import { useIsMounted } from '../../utils/useIsMounted';
import { ModalProps, ModalType } from './AdminManageSupportPlans.types';
import { ManageSupportPlanModal } from './components/ManageSupportPlanModal';

export const AdminManageSupportPlansPage = memo(() => {
  const [modal, setModal] = useState<ModalProps | null>(null);
  const { t } = useTranslation([Namespaces.Admin, Namespaces.Common]);
  const isMounted = useIsMounted();
  const miscClasses = useMiscellaneousStyles();
  const {
    data: supportPlansData,
    isLoading,
    error,
  } = useQuery(fetchSupportPlans.key, fetchSupportPlans.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const [deletePlanMutate, { isLoading: isProcessing }] = useMutation(
    SuperuserSupportPlanApiService.deleteSupportPlan,
    {
      onSuccess: async () => await reactToAdminSupportPlanAction(),
      onError: (error: ApiClientError) => {
        if (!isMounted.current) {
          return;
        }
        setModal({ type: ModalType.ERROR, error: error.message });
      },
    },
  );

  const systemConfigQuery = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn);

  const systemConfig = useMemo(
    () => systemConfigQuery.data?.payload ?? ({} as SystemConfiguration),
    [systemConfigQuery.data],
  );

  const supportPlans = useMemo(() => supportPlansData?.payload ?? [], [supportPlansData]);

  const getStatusIcon = useCallback(
    (status: boolean | undefined) =>
      status ? <CheckCircle className={miscClasses.successColor} /> : <Cancel className={miscClasses.failureColor} />,
    [miscClasses.failureColor, miscClasses.successColor],
  );

  const handleAddClick = useCallback(() => setModal({ type: ModalType.CREATE }), []);

  const handleManageClick = useCallback(
    (_: any, rowData: SupportPlanResponse | SupportPlanResponse[]) => (type: ModalType) => {
      const supportPlan = Array.isArray(rowData) ? rowData[0] : rowData;
      setModal({ type, supportPlan });
    },
    [],
  );

  const onDeletePlan = useCallback(
    async (planId?: string) => {
      if (!planId) {
        return;
      }
      await deletePlanMutate({ planId });
    },
    [deletePlanMutate],
  );

  const handleCloseModal = useCallback(() => setModal(null), []);

  const onPlanManageSuccess = useCallback(async () => {
    await reactToAdminSupportPlanAction();
    handleCloseModal();
  }, [handleCloseModal]);

  const getLabelKey = useCallback((key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  }, []);

  const getActionLabelKey = useCallback((key: string): string => {
    return `manage_plans.dialogs.actions.${key}`;
  }, []);

  const getDisableReasonLabelKey = useCallback((key: string): string => {
    return `manage_plans.disabled_reasons.${key}`;
  }, []);

  const tableConfig = useMemo<HUMaterialTableProps<SupportPlanResponse>>(
    () => ({
      columns: [
        {
          title: t(getLabelKey('id')),
          field: 'id',
        },
        {
          title: t(getLabelKey('name')),
          field: 'name',
        },
        {
          title: t(getLabelKey('description')),
          field: 'description',
        },
        {
          title: t(getLabelKey('price')),
          render: rowData =>
            t(`${Namespaces.Common}:format_currency.currencyShort`, {
              value: rowData.price,
              currency: systemConfig.currency,
            }),
        },
        {
          title: t(getLabelKey('is_default')),
          render: rowData => getStatusIcon(rowData.isDefault),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
      ],
      data: supportPlans,
      title: t('manage_plans.support_plans.title'),
      actions: [
        {
          icon: () => <Add />,
          tooltip: t(getActionLabelKey('add')),
          isFreeAction: true,
          onClick: handleAddClick,
        },
        {
          icon: () => <Edit />,
          tooltip: t(getActionLabelKey('edit')),
          onClick: (e, rowData) => handleManageClick(e, rowData)(ModalType.EDIT),
        },
        rowData => ({
          icon: () => <Delete />,
          tooltip: rowData.isDefault ? t(getDisableReasonLabelKey('plan_is_default')) : t(getActionLabelKey('delete')),
          onClick: (e, rowData) => handleManageClick(e, rowData)(ModalType.DELETE),
          disabled: rowData.isDefault,
        }),
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
    }),
    [
      getActionLabelKey,
      getDisableReasonLabelKey,
      getLabelKey,
      handleAddClick,
      handleManageClick,
      getStatusIcon,
      supportPlans,
      t,
      systemConfig,
    ],
  );

  const modals = {
    [ModalType.CREATE]: (
      <ManageSupportPlanModal type={ModalType.CREATE} onClose={handleCloseModal} onSuccess={onPlanManageSuccess} />
    ),
    [ModalType.EDIT]: (
      <ManageSupportPlanModal
        type={ModalType.EDIT}
        supportPlan={modal?.supportPlan}
        onClose={handleCloseModal}
        onSuccess={onPlanManageSuccess}
      />
    ),
    [ModalType.ERROR]: <SimpleErrorModal open={!!modal} message={modal?.error} onOk={handleCloseModal} />,
    [ModalType.DELETE]: (
      <SimpleConfirmModal
        open={!!modal}
        {...(modal
          ? {
              onConfirm: () => {
                void onDeletePlan(modal?.supportPlan?.id);
                handleCloseModal();
              },
              onCancel: handleCloseModal,
              message: t('manage_plans.dialogs.confirmation.delete_plan', {
                planName: modal?.supportPlan?.name,
              }),
            }
          : { onConfirm: () => {}, onCancel: () => {}, message: '' })}
      />
    ),
  };

  return (
    <>
      {isLoading ? (
        <AlignCenter className={miscClasses.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : error ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isProcessing} delay={50}>
          <HUMaterialTable {...tableConfig} />
        </HUSpin>
      )}
      {modal && modals[modal.type]}
    </>
  );
});
